import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  QrCode as QrIcon, Plus, Trash2, Download, Upload, BarChart3, Image as ImageIcon,
  ExternalLink, Loader2, X, Copy, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_HOST = 'https://clearcommunicationapp.com';

const KIND_LABEL = { url: 'URL', vcard: 'vCard', text: 'Text' };

/** Form for the create dialog. Three kinds, one form. */
const CreateQrDialog = ({ open, onClose, onCreated }) => {
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('url');
  const [targetUrl, setTargetUrl] = useState('');
  const [rawData, setRawData] = useState('');
  const [vcard, setVcard] = useState({
    first_name: '', last_name: '', organization: '', title: '',
    phone: '', email: '', url: '', note: '',
  });
  const [fgColor, setFgColor] = useState('#0d1f2d');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [submitting, setSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const logoInputRef = React.useRef(null);

  const onLogoSelect = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Logo must be an image');
    if (file.size > 2 * 1024 * 1024) return toast.error('Logo max 2 MB');
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const clearStagedLogo = () => {
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview('');
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const reset = () => {
    setLabel(''); setKind('url'); setTargetUrl(''); setRawData('');
    setVcard({ first_name: '', last_name: '', organization: '', title: '', phone: '', email: '', url: '', note: '' });
    setFgColor('#0d1f2d'); setBgColor('#ffffff');
    clearStagedLogo();
  };

  const submit = async () => {
    if (!label.trim()) return toast.error('Label required');
    if (kind === 'url' && !/^https?:\/\//.test(targetUrl)) return toast.error('URL must start with http:// or https://');
    if (kind === 'text' && !rawData.trim()) return toast.error('Text data required');
    if (kind === 'vcard' && !vcard.first_name && !vcard.last_name && !vcard.organization) {
      return toast.error('vCard needs at least a name or organization');
    }

    setSubmitting(true);
    const body = {
      label: label.trim(),
      kind,
      fg_color: fgColor,
      bg_color: bgColor,
      target_url: kind === 'url' ? targetUrl.trim() : null,
      raw_data: kind === 'text' ? rawData.trim() : null,
      vcard: kind === 'vcard' ? vcard : null,
    };
    const { data, error } = await apiFetch('/api/admin/qr', { method: 'POST', body });
    if (error) {
      setSubmitting(false);
      return toast.error(error);
    }
    // Upload logo if user attached one in the create dialog
    if (logoFile && data?.qr_id) {
      const fd = new FormData();
      fd.append('file', logoFile);
      const token = localStorage.getItem('auth_token');
      try {
        const res = await fetch(`${API}/api/admin/qr/${data.qr_id}/logo`, {
          method: 'POST',
          body: fd,
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          toast.warning(`QR created but logo upload failed: ${j?.detail || res.status}`);
        }
      } catch (e) {
        toast.warning(`QR created but logo upload errored: ${e.message}`);
      }
    }
    setSubmitting(false);
    toast.success(`QR "${data.label}" created`);
    reset();
    onCreated(data);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="qr-create-dialog">
        <DialogHeader>
          <DialogTitle>Create QR code</DialogTitle>
          <DialogDescription>
            Choose what the QR encodes. URL and vCard scans are tracked; text QRs are stored locally without tracking.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="qr-label">Label (internal name)</Label>
            <Input
              id="qr-label" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Conference flyer QR — March 2026"
              data-testid="qr-label-input"
            />
          </div>

          <Tabs value={kind} onValueChange={setKind}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="url" data-testid="qr-kind-url">URL</TabsTrigger>
              <TabsTrigger value="vcard" data-testid="qr-kind-vcard">vCard</TabsTrigger>
              <TabsTrigger value="text" data-testid="qr-kind-text">Text</TabsTrigger>
            </TabsList>

            <TabsContent value="url" className="mt-3">
              <Label htmlFor="qr-url">Target URL</Label>
              <Input
                id="qr-url" placeholder="https://clearcommunicationapp.com/links"
                value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)}
                data-testid="qr-url-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Scan = 302 redirect to this URL. Counts every scan.
              </p>
            </TabsContent>

            <TabsContent value="vcard" className="mt-3 grid grid-cols-2 gap-3">
              {[
                ['first_name', 'First name'],
                ['last_name', 'Last name'],
                ['organization', 'Organization'],
                ['title', 'Title / role'],
                ['phone', 'Phone'],
                ['email', 'Email'],
                ['url', 'Website URL'],
              ].map(([k, lab]) => (
                <div key={k}>
                  <Label htmlFor={`vc-${k}`} className="text-xs">{lab}</Label>
                  <Input
                    id={`vc-${k}`}
                    value={vcard[k]}
                    onChange={(e) => setVcard((v) => ({ ...v, [k]: e.target.value }))}
                    data-testid={`vc-${k}`}
                  />
                </div>
              ))}
              <div className="col-span-2">
                <Label htmlFor="vc-note" className="text-xs">Note</Label>
                <Textarea
                  id="vc-note" rows={2} value={vcard.note}
                  onChange={(e) => setVcard((v) => ({ ...v, note: e.target.value }))}
                  data-testid="vc-note"
                />
              </div>
            </TabsContent>

            <TabsContent value="text" className="mt-3">
              <Label htmlFor="qr-text">Raw data</Label>
              <Textarea
                id="qr-text" rows={4}
                placeholder="Any text, Wi-Fi config (WIFI:T:WPA;S:MyNet;P:pass;;), etc."
                value={rawData} onChange={(e) => setRawData(e.target.value)}
                data-testid="qr-text-input"
              />
            </TabsContent>
          </Tabs>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Foreground color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color" value={fgColor} onChange={(e) => setFgColor(e.target.value)}
                  className="w-10 h-10 border rounded"
                />
                <Input value={fgColor} onChange={(e) => setFgColor(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Background color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)}
                  className="w-10 h-10 border rounded"
                />
                <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Optional center logo */}
          <div className="space-y-2 rounded-md border p-3 bg-muted/30">
            <Label className="text-xs flex items-center gap-1.5">
              <ImageIcon className="h-3.5 w-3.5" /> Center logo (optional)
            </Label>
            <p className="text-[11px] text-muted-foreground">
              PNG/JPG/WEBP, ≤2 MB. Sized to ~22% of the QR with a white backdrop so scanning still works.
            </p>
            <div className="flex items-center gap-3">
              {logoPreview ? (
                <div className="relative h-16 w-16 rounded border bg-white flex items-center justify-center overflow-hidden shrink-0">
                  <img src={logoPreview} alt="Logo preview" className="max-h-full max-w-full" />
                </div>
              ) : (
                <div className="h-16 w-16 rounded border border-dashed border-muted-foreground/30 flex items-center justify-center shrink-0 text-muted-foreground">
                  <ImageIcon className="h-5 w-5" />
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => onLogoSelect(e.target.files?.[0])}
                  data-testid="qr-create-logo-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => logoInputRef.current?.click()}
                  data-testid="qr-create-logo-btn"
                >
                  <Upload className="h-3 w-3 mr-1" />
                  {logoFile ? 'Replace logo' : 'Add image / logo'}
                </Button>
                {logoFile && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={clearStagedLogo}
                    data-testid="qr-create-logo-clear"
                  >
                    <X className="h-3 w-3 mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
            data-testid="qr-create-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatsDialog = ({ qr, onClose }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!qr) return;
    let cancelled = false;
    apiFetch(`/api/admin/qr/${qr.qr_id}/stats?days=30`).then(({ data }) => {
      if (!cancelled) setStats(data);
    });
    return () => { cancelled = true; };
  }, [qr]);

  if (!qr) return null;
  const max = stats?.daily_scans ? Math.max(...stats.daily_scans.map((d) => d.count), 1) : 1;

  return (
    <Dialog open={!!qr} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="qr-stats-dialog">
        <DialogHeader>
          <DialogTitle>Scan stats — {qr.label}</DialogTitle>
          <DialogDescription>
            {stats?.scan_count ?? '–'} total scans
            {stats?.last_scanned_at && <> · last {new Date(stats.last_scanned_at).toLocaleString()}</>}
          </DialogDescription>
        </DialogHeader>
        {!stats ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="flex items-end gap-1 h-32 px-1 border rounded p-2 bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
              {stats.daily_scans.map((d) => (
                <div
                  key={d.day}
                  title={`${d.day}: ${d.count}`}
                  className="flex-1 bg-[#00E5CC] rounded-t min-h-[2px]"
                  style={{ height: `${(d.count / max) * 100}%` }}
                />
              ))}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 flex justify-between mt-1">
              <span>{stats.daily_scans[0]?.day}</span>
              <span>30 days</span>
              <span>{stats.daily_scans[stats.daily_scans.length - 1]?.day}</span>
            </div>

            <div className="mt-4">
              <p className="text-sm font-medium mb-2">Recent scans</p>
              <div className="border rounded max-h-60 overflow-auto dark:border-slate-700">
                {stats.recent.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400 p-4 text-center">No scans yet</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                      <tr><th className="text-left p-2">When</th><th className="text-left p-2">Device</th></tr>
                    </thead>
                    <tbody>
                      {stats.recent.map((s, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2 whitespace-nowrap">{new Date(s.ts).toLocaleString()}</td>
                          <td className="p-2 truncate max-w-xs text-slate-600">{s.user_agent}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const QrCard = ({ qr, onRefresh, onShowStats }) => {
  const [busy, setBusy] = useState(false);
  const [imgVersion, setImgVersion] = useState(Date.now());
  const fileInputRef = React.useRef(null);
  // Use the PUBLIC slug-keyed endpoint for the <img> + download — it
  // doesn't require auth so the browser's bare <img> tag (which can't
  // send a Bearer token) loads correctly. The admin /png route exists
  // for back-compat but 401s on raw <img> requests.
  const imgSrc = `${API}/api/qr/img/${qr.slug}.png?v=${imgVersion}`;
  const publicScanUrl = `${PUBLIC_HOST}/api/qr/r/${qr.slug}`;

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Must be an image');
    if (file.size > 2 * 1024 * 1024) return toast.error('Max 2 MB');
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/admin/qr/${qr.qr_id}/logo`, {
        method: 'POST', body: fd, credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Upload failed');
      toast.success('Logo added — regenerating QR');
      setImgVersion(Date.now());
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const clearLogo = async () => {
    setBusy(true);
    const { error } = await apiFetch(`/api/admin/qr/${qr.qr_id}/logo`, { method: 'DELETE' });
    setBusy(false);
    if (error) return toast.error(error);
    toast.success('Logo cleared');
    setImgVersion(Date.now());
    onRefresh();
  };

  const remove = async () => {
    if (!window.confirm(`Delete QR "${qr.label}"? Scans will also be deleted.`)) return;
    const { error } = await apiFetch(`/api/admin/qr/${qr.qr_id}`, { method: 'DELETE' });
    if (error) return toast.error(error);
    toast.success('QR deleted');
    onRefresh();
  };

  const downloadPng = async () => {
    try {
      const res = await fetch(imgSrc);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${qr.label.replace(/\W+/g, '_') || qr.slug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || 'Download failed');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(publicScanUrl).then(
      () => toast.success('Scan URL copied'),
      () => toast.error('Copy failed'),
    );
  };

  return (
    <Card data-testid={`qr-card-${qr.qr_id}`}>
      {/* Stack image + details vertically on mobile so the 128px QR
          and the long scan URL/buttons all fit a 360px viewport. */}
      <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
        <div className="flex-shrink-0 w-32 h-32 mx-auto sm:mx-0 bg-white border rounded overflow-hidden flex items-center justify-center">
          <img src={imgSrc} alt={qr.label} className="w-full h-full object-contain" data-testid={`qr-img-${qr.qr_id}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 w-full">
              <h3 className="font-medium truncate" data-testid={`qr-label-${qr.qr_id}`}>{qr.label}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="outline">{KIND_LABEL[qr.kind] || qr.kind}</Badge>
                <Badge className="bg-[#00E5CC]/10 text-[#0d1f2d] border-[#00E5CC]/30">
                  {qr.scan_count || 0} scan{qr.scan_count === 1 ? '' : 's'}
                </Badge>
              </div>
              {qr.kind === 'url' && (
                <p className="text-xs text-slate-500 mt-2 truncate">→ {qr.target_url}</p>
              )}
              <button
                onClick={copyLink}
                className="text-xs text-slate-500 mt-1 flex items-center gap-1 hover:text-[#00A896] w-full max-w-full"
                data-testid={`qr-copy-${qr.qr_id}`}
                title={publicScanUrl}
              >
                <Copy className="h-3 w-3 shrink-0" />
                <span className="truncate">{publicScanUrl}</span>
              </button>
            </div>
          </div>

          <div className="flex gap-2 mt-3 flex-wrap">
            <Button size="sm" variant="outline" onClick={downloadPng} data-testid={`qr-download-${qr.qr_id}`}>
              <Download className="h-3 w-3 mr-1" /> PNG
            </Button>
            <Button size="sm" variant="outline" onClick={() => onShowStats(qr)} data-testid={`qr-stats-${qr.qr_id}`}>
              <BarChart3 className="h-3 w-3 mr-1" /> Stats
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              data-testid={`qr-upload-logo-${qr.qr_id}`}
            >
              {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
              {qr.logo_path ? 'Replace logo' : 'Add logo'}
            </Button>
            {qr.logo_path && (
              <Button size="sm" variant="ghost" onClick={clearLogo} data-testid={`qr-clear-logo-${qr.qr_id}`}>
                <X className="h-3 w-3 mr-1" /> Clear logo
              </Button>
            )}
            <Button
              size="sm" variant="ghost"
              onClick={() => { setImgVersion(Date.now()); toast.success('Refreshed'); }}
              data-testid={`qr-refresh-${qr.qr_id}`}
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 ml-auto" onClick={remove} data-testid={`qr-delete-${qr.qr_id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
            <input
              type="file" ref={fileInputRef} className="hidden"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => handleLogoUpload(e.target.files?.[0])}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const QrCodesTab = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [statsFor, setStatsFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiFetch('/api/admin/qr');
    setLoading(false);
    if (error) return toast.error(error);
    setCodes(data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="qr-codes-tab">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <QrIcon className="h-5 w-5 text-[#00A896] shrink-0" /> QR Code Generator
            </CardTitle>
            <CardDescription>
              vCard, URL, or text QR codes with scan tracking and optional center-logo overlay.
            </CardDescription>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd] shrink-0 self-start sm:self-auto"
            data-testid="qr-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New QR
          </Button>
        </CardHeader>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : codes.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">
          <QrIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No QR codes yet. Click "New QR" to create one.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {codes.map((qr) => (
            <QrCard key={qr.qr_id} qr={qr} onRefresh={load} onShowStats={setStatsFor} />
          ))}
        </div>
      )}

      <CreateQrDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={(d) => { setShowCreate(false); load(); }} />
      <StatsDialog qr={statsFor} onClose={() => setStatsFor(null)} />
    </div>
  );
};

export default QrCodesTab;
